import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  orderBy,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { generateUniqueSlug } from '../utils/slug';

// Firestore collection referansları
export const COLLECTIONS = {
  PRODUCTS: 'products',
  CATEGORIES: 'categories'
} as const;

// Tip tanımlamaları
export interface FirestoreProduct {
  id?: string;
  title: string;
  description: string;
  category: string;
  image: string;
  slug?: string; // URL-friendly ürün slug'ı
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface FirestoreCategory {
  id?: string;
  name: string;
  slug: string;
  description?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

// PRODUCT CRUD İşlemleri
export const productService = {
  // Tüm ürünleri getir
  async getAll(): Promise<FirestoreProduct[]> {
    try {
      const productsRef = collection(db, COLLECTIONS.PRODUCTS);
      const q = query(productsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as FirestoreProduct));
    } catch (error) {
      console.error('Ürünler getirilirken hata:', error);
      throw new Error('Ürünler getirilemedi');
    }
  },

  // Kategori ile ürünleri getir (slug ile arama)
  async getByCategory(categorySlug: string): Promise<FirestoreProduct[]> {
    try {
      
      const productsRef = collection(db, COLLECTIONS.PRODUCTS);
      
      // Index gerektirmeyen basit query (orderBy kaldırıldı)
      let q = query(
        productsRef, 
        where('category', '==', categorySlug)
      );
      let querySnapshot = await getDocs(q);
      
      // Eğer slug ile bulunamazsa, kategori name ile dene
      if (querySnapshot.docs.length === 0) {
        
        // Kategori adını slug\'dan çıkar (kebab-case to Title Case)
        const categoryName = categorySlug
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        q = query(
          productsRef, 
          where('category', '==', categoryName)
        );
        querySnapshot = await getDocs(q);
        
      }
      
      // Sonuç bulunamazsa tüm ürünleri kontrol et
      if (querySnapshot.docs.length === 0) {
        const allProducts = await this.getAll();
        
        allProducts.forEach(product => {
        });
        
        return [];
      }

      // Sonuçları tarih sırasına göre manuel sırala (createdAt kullanmadan)
      const products = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as FirestoreProduct));
      
      // Manuel tarihe göre sıralama (eğer createdAt varsa)
      products.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.toDate().getTime() - a.createdAt.toDate().getTime();
        }
        return 0;
      });
      
      return products;
      
    } catch (error) {
      console.error('❌ Kategori ürünleri getirilirken hata:', error);
      throw new Error('Kategori ürünleri getirilemedi');
    }
  }, 

  // Tekil ürün getir
  async getById(id: string): Promise<FirestoreProduct | null> {
    try {
      const docRef = doc(db, COLLECTIONS.PRODUCTS, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as FirestoreProduct;
      }
      return null;
    } catch (error) {
      console.error('Ürün getirilirken hata:', error);
      throw new Error('Ürün getirilemedi');
    }
  },

  // Slug ile ürün getir
  async getBySlug(slug: string): Promise<FirestoreProduct | null> {
    try {
      const productsRef = collection(db, COLLECTIONS.PRODUCTS);
      const q = query(productsRef, where('slug', '==', slug));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      } as FirestoreProduct;
    } catch (error) {
      console.error('Ürün slug ile getirilirken hata:', error);
      throw new Error('Ürün bulunamadı');
    }
  },

  // Yeni ürün ekle
  async create(product: Omit<FirestoreProduct, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const now = Timestamp.now();

      const uniqueSlug = await generateUniqueSlug(
        product.slug || product.title,
        async (slug) => {
          const existing = await productService.getBySlug(slug);
          return existing !== null;
        }
      );

      const productData = {
        ...product,
        slug: uniqueSlug,
        createdAt: now,
        updatedAt: now
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.PRODUCTS), productData);
      return docRef.id;
    } catch (error) {
      console.error('Ürün eklenirken hata:', error);
      throw new Error('Ürün eklenemedi');
    }
  },

  // Ürün güncelle
  async update(id: string, updates: Partial<Omit<FirestoreProduct, 'id' | 'createdAt'>>): Promise<void> {
    try {
      const docRef = doc(db, COLLECTIONS.PRODUCTS, id);

      let finalUpdates = { ...updates };
      if (updates.slug) {
        finalUpdates.slug = await generateUniqueSlug(
          updates.slug,
          async (slug) => {
            const existing = await productService.getBySlug(slug);
            return existing !== null && existing.id !== id;
          }
        );
      }

      const updateData = {
        ...finalUpdates,
        updatedAt: Timestamp.now()
      };

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Ürün güncellenirken hata:', error);
      throw new Error('Ürün güncellenemedi');
    }
  },

  // Ürün sil
  async delete(id: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTIONS.PRODUCTS, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Ürün silinirken hata:', error);
      throw new Error('Ürün silinemedi');
    }
  }
};

// CATEGORY CRUD İşlemleri
export const categoryService = {
  // Tüm kategorileri getir
  async getAll(): Promise<FirestoreCategory[]> {
    try {
      const categoriesRef = collection(db, COLLECTIONS.CATEGORIES);
      const q = query(categoriesRef, orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as FirestoreCategory));
    } catch (error) {
      console.error('Kategoriler getirilirken hata:', error);
      throw new Error('Kategoriler getirilemedi');
    }
  },

  // Slug ile kategori getir
  async getBySlug(slug: string): Promise<FirestoreCategory | null> {
    try {
      const categoriesRef = collection(db, COLLECTIONS.CATEGORIES);
      const q = query(categoriesRef, where('slug', '==', slug));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      } as FirestoreCategory;
    } catch (error) {
      console.error('Kategori slug ile getirilirken hata:', error);
      throw new Error('Kategori bulunamadı');
    }
  },

  // Yeni kategori ekle
  async create(category: Omit<FirestoreCategory, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const now = Timestamp.now();
      const categoryData = {
        ...category,
        createdAt: now,
        updatedAt: now
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.CATEGORIES), categoryData);
      return docRef.id;
    } catch (error) {
      console.error('Kategori eklenirken hata:', error);
      throw new Error('Kategori eklenemedi');
    }
  },

  // Kategori güncelle
  async update(id: string, updates: Partial<Omit<FirestoreCategory, 'id' | 'createdAt'>>): Promise<void> {
    try {
      const docRef = doc(db, COLLECTIONS.CATEGORIES, id);
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now()
      };

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Kategori güncellenirken hata:', error);
      throw new Error('Kategori güncellenemedi');
    }
  },

  // Kategori sil
  async delete(id: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTIONS.CATEGORIES, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Kategori silinirken hata:', error);
      throw new Error('Kategori silinemedi');
    }
  }
};